/**
 * The exchange: six boxes, each holding one offer to buy or to sell.
 *
 * An offer is a standing promise rather than a trade: what it costs is handed
 * over as it is made, and whatever it earns waits in its box until it is
 * collected. Nothing here decides anything; every press is a request and what
 * comes back is what the window draws.
 */

import { useState } from 'react';
import type { ExchangeWire, ItemDefinitionWire } from '@inverse/protocol';
import { CloseButton } from './CloseButton';
import { useDraggableWindow } from './draggable';

const SLOTS = 6;

export function Exchange({
  state,
  items,
  describe,
  onOffer,
  onAsk,
  onCancel,
  onCollect,
  onClose,
}: {
  state: ExchangeWire;
  /** Everything there is, for naming what to buy. */
  items: readonly ItemDefinitionWire[];
  describe: (itemId: number) => ItemDefinitionWire | undefined;
  onOffer: (
    slot: number,
    itemId: number,
    selling: boolean,
    price: number,
    quantity: number,
  ) => void;
  /** Asks what one thing has been going for, while an offer is being written. */
  onAsk: (itemId: number) => void;
  onCancel: (slot: number) => void;
  onCollect: (slot: number) => void;
  onClose: () => void;
}): JSX.Element {
  const { attach, style, onPointerDown } = useDraggableWindow('exchange');
  const [making, setMaking] = useState<{ slot: number; selling: boolean } | null>(null);
  const [search, setSearch] = useState('');
  const [chosen, setChosen] = useState<ItemDefinitionWire | null>(null);
  const [price, setPrice] = useState('');
  const [quantity, setQuantity] = useState('1');

  const shut = (): void => {
    setMaking(null);
    setChosen(null);
    setSearch('');
    setPrice('');
    setQuantity('1');
  };

  const matches = search.trim()
    ? items
        .filter((one) => one.name.toLowerCase().includes(search.trim().toLowerCase()))
        .slice(0, 8)
    : [];

  return (
    <div className="trade exchange" ref={attach} style={style}>
      <div className="panel-head drag-handle" onPointerDown={onPointerDown}>
        <span>Exchange</span>
        <CloseButton onClose={onClose} />
      </div>

      {making === null ? (
        <div className="panel-body exchange-slots">
          {Array.from({ length: SLOTS }, (_, slot) => {
            const offer = state.offers.find((one) => one.slot === slot);
            if (!offer) {
              return (
                <div className="exchange-box empty" key={slot}>
                  <button type="button" onClick={() => setMaking({ slot, selling: false })}>
                    Buy
                  </button>
                  <button type="button" onClick={() => setMaking({ slot, selling: true })}>
                    Sell
                  </button>
                </div>
              );
            }

            const name = describe(offer.itemId)?.name ?? `Item ${offer.itemId}`;
            const waiting = offer.owedItems > 0 || offer.owedCoins > 0;
            const part = offer.quantity > 0 ? offer.filled / offer.quantity : 1;
            return (
              <div className={offer.open ? 'exchange-box' : 'exchange-box done'} key={slot}>
                <div className="exchange-what">
                  <strong>
                    {offer.quantity > 0 ? (offer.selling ? 'Selling' : 'Buying') : 'Waiting'}
                  </strong>
                  <span>{offer.quantity > 0 ? name : 'Collect what is here'}</span>
                </div>
                {offer.quantity > 0 ? (
                  <>
                    <div className="exchange-bar">
                      <span style={{ width: `${Math.round(part * 100)}%` }} />
                    </div>
                    <small>
                      {offer.filled} of {offer.quantity} at {offer.price.toLocaleString()} each
                      {state.rates[String(offer.itemId)] !== undefined
                        ? ` · lately ${state.rates[String(offer.itemId)]?.toLocaleString()}`
                        : ''}
                    </small>
                  </>
                ) : null}
                {waiting ? (
                  <small className="exchange-owed">
                    Waiting: {offer.owedItems > 0 ? `${offer.owedItems} ${name}` : ''}
                    {offer.owedItems > 0 && offer.owedCoins > 0 ? ', ' : ''}
                    {offer.owedCoins > 0 ? `${offer.owedCoins.toLocaleString()} coins` : ''}
                  </small>
                ) : null}
                <div className="row">
                  {waiting || !offer.open ? (
                    <button type="button" onClick={() => onCollect(slot)}>
                      Collect
                    </button>
                  ) : null}
                  {offer.open && offer.quantity > 0 ? (
                    <button type="button" onClick={() => onCancel(slot)}>
                      Call it off
                    </button>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="panel-body exchange-making">
          <h4>{making.selling ? 'Selling' : 'Buying'}</h4>
          {chosen ? (
            <p className="hint">
              {chosen.name}
              {state.rates[String(chosen.id)] !== undefined
                ? ` · lately ${state.rates[String(chosen.id)]?.toLocaleString()} coins`
                : ` · worth about ${chosen.value.toLocaleString()} coins`}
            </p>
          ) : (
            <>
              <input
                type="text"
                className="editor-search"
                placeholder="What is it called"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
              <div className="exchange-matches">
                {matches.map((one) => (
                  <button
                    key={one.id}
                    type="button"
                    onClick={() => {
                      setChosen(one);
                      setPrice(String(state.rates[String(one.id)] ?? Math.max(1, one.value)));
                      onAsk(one.id);
                    }}
                  >
                    {one.name}
                  </button>
                ))}
              </div>
            </>
          )}

          {chosen ? (
            <>
              <label className="editor-field">
                <span>How many</span>
                <input
                  type="number"
                  min={1}
                  value={quantity}
                  onChange={(event) => setQuantity(event.target.value)}
                />
              </label>
              <label className="editor-field">
                <span>Coins each</span>
                <input
                  type="number"
                  min={1}
                  value={price}
                  onChange={(event) => setPrice(event.target.value)}
                />
              </label>
              <p className="hint">
                {making.selling ? 'You hand over the goods now.' : 'You hand over the coins now.'}{' '}
                {(
                  Math.max(1, Number(price) || 1) * Math.max(1, Number(quantity) || 1)
                ).toLocaleString()}{' '}
                coins in all.
              </p>
            </>
          ) : null}

          <div className="row">
            <button
              type="button"
              className="primary"
              disabled={!chosen}
              onClick={() => {
                if (!chosen) return;
                onOffer(
                  making.slot,
                  chosen.id,
                  making.selling,
                  Math.max(1, Math.round(Number(price) || 1)),
                  Math.max(1, Math.round(Number(quantity) || 1)),
                );
                shut();
              }}
            >
              Put it up
            </button>
            <button type="button" onClick={shut}>
              Back
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
